import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { LayerType } from '../../types/model.ts';
import type { PhotometricTarget } from '../../utils/photometricTargets';
import type { MeasurementData } from './workspace.reducer.ts';

/** Frontend-only, in-memory measurement planes (calculation surfaces drawn over the model).
 *  Not persisted yet — a hard refresh clears them (backend is a later phase). */

export type PlaneVec2 = [number, number];
export type PlaneVec3 = [number, number, number];

/** Oriented plane basis. Present for non-horizontal (e.g. façade) surfaces. */
export interface PlaneFrame {
  origin: PlaneVec3;
  normal: PlaneVec3;
  u: PlaneVec3;
  v: PlaneVec3;
}

export interface MeasurementPlane {
  id: string;
  name: string;
  type: LayerType;
  visible: boolean;
  /** Height (z, metres) for horizontal planes. Ignored when `frame` is set. */
  height: number;
  normId: number | null;
  normName: string | null;
  /** Individual photometric targets. Set instead of normId. */
  customTargets?: PhotometricTarget[] | null;
  /**
   * Polygon vertices. Horizontal (no `frame`): world XY. Oriented (`frame` set):
   * (u, v) coordinates in the frame.
   */
  polygon: PlaneVec2[];
  /** Set for oriented (vertical/sloped) surfaces; absent = horizontal at `height`. */
  frame?: PlaneFrame;
  /**
   * Camera position when the surface was drawn (world). For oriented (façade) surfaces the
   * points are draped by raycasting FROM here, so they land on the exact surface the user
   * was looking at — following protrusions/recesses instead of a flat plane.
   */
  cameraPos?: PlaneVec3;
  /** Measurement-point spacing (m) for this surface. Falls back to the global spacing. */
  spacing?: number;
  /**
   * Cut-out regions subtracted from the surface (e.g. the centre of a roundabout).
   * Each hole is a polygon in the SAME coordinate space as `polygon` (world XY for
   * horizontal, frame (u,v) for oriented). Circles/rectangles are tessellated to polygons.
   */
  exclusions?: PlaneVec2[][];
}

/** Shape used while drawing an exclusion cut-out. */
type ExclusionShape = 'polygon' | 'circle' | 'rectangle';

/** Which measurement points the scene shows — never both at once. */
export type PointsView = 'model' | 'surface';

interface MeasurementPlanesState {
  planes: MeasurementPlane[];
  draw: {
    active: boolean;
    /** Vertices of the polygon currently being drawn (world XY). */
    verts: PlaneVec2[];
    /** Highest model-surface z hit so far — becomes the committed plane height. */
    height: number;
    /** Oriented frame, set on the first click when the hit surface is non-horizontal. */
    frame: PlaneFrame | null;
  };
  /** In-progress exclusion cut-out (drawn onto an existing surface). */
  exclusionDraw: {
    active: boolean;
    /** Surface being cut. */
    planeId: string | null;
    shape: ExclusionShape;
    /** Draft vertices in the target surface's coordinate space. */
    verts: PlaneVec2[];
  };
  selectedId: string | null;
  /** Computed photometry per plane (uuid === plane id). Frontend-only, in-memory. */
  results: MeasurementData[];
  /**
   * Which measurement points are shown: the whole-model measurement ('model') OR the
   * drawn-surface points ('surface'). Mutually exclusive — never both at once.
   */
  pointsView: PointsView;
  /** True while the surface photometry worker is running (drives the "Calculating…" hint). */
  computing: boolean;
  /** Just-drawn surface awaiting a norm choice — drives the floating norm popup at (x, y). */
  normPrompt: { planeId: string; x: number; y: number } | null;
  /** Undo stack of previous `planes` snapshots (most recent last). */
  past: MeasurementPlane[][];
}

const HISTORY_LIMIT = 50;
const snapshot = (planes: MeasurementPlane[]): MeasurementPlane[] => JSON.parse(JSON.stringify(planes));

const initialState: MeasurementPlanesState = {
  planes: [],
  draw: { active: false, verts: [], height: 0, frame: null },
  exclusionDraw: { active: false, planeId: null, shape: 'polygon', verts: [] },
  selectedId: null,
  results: [],
  pointsView: 'surface',
  computing: false,
  normPrompt: null,
  past: [],
};

export const measurementPlanesReducer = createSlice({
  name: 'measurementPlanes',
  initialState,
  reducers: {
    setPlaneDrawActive: (state, action: PayloadAction<boolean>) => {
      state.draw.active = action.payload;
      state.draw.verts = [];
      state.draw.height = 0;
      state.draw.frame = null;
      state.normPrompt = null;
    },
    setPlaneComputing: (state, action: PayloadAction<boolean>) => {
      state.computing = action.payload;
    },
    setNormPrompt: (state, action: PayloadAction<{ planeId: string; x: number; y: number } | null>) => {
      state.normPrompt = action.payload;
    },
    pushPlaneVertex: (state, action: PayloadAction<{ xy: PlaneVec2; z: number }>) => {
      state.draw.verts.push(action.payload.xy);
      state.draw.height = Math.max(state.draw.height, action.payload.z);
    },
    setPlaneDrawFrame: (state, action: PayloadAction<PlaneFrame | null>) => {
      state.draw.frame = action.payload;
    },
    popPlaneVertex: (state) => {
      state.draw.verts.pop();
    },
    pushPlaneHistory: (state) => {
      state.past.push(snapshot(state.planes));
      if (state.past.length > HISTORY_LIMIT) state.past.shift();
    },
    undoPlane: (state) => {
      const prev = state.past.pop();
      if (prev) {
        state.planes = prev;
        if (!state.planes.some((p) => p.id === state.selectedId)) state.selectedId = null;
      }
    },
    addPlane: (state, action: PayloadAction<MeasurementPlane>) => {
      state.past.push(snapshot(state.planes));
      if (state.past.length > HISTORY_LIMIT) state.past.shift();
      state.planes.push(action.payload);
      state.selectedId = action.payload.id;
      state.draw.verts = [];
      state.draw.active = false;
      state.draw.height = 0;
      state.draw.frame = null;
    },
    insertPlaneVertex: (state, action: PayloadAction<{ id: string; index: number; xy: PlaneVec2 }>) => {
      const plane = state.planes.find((p) => p.id === action.payload.id);
      if (!plane) return;
      state.past.push(snapshot(state.planes));
      if (state.past.length > HISTORY_LIMIT) state.past.shift();
      plane.polygon.splice(action.payload.index, 0, action.payload.xy);
    },
    deletePlaneVertex: (state, action: PayloadAction<{ id: string; index: number }>) => {
      const plane = state.planes.find((p) => p.id === action.payload.id);
      if (!plane || plane.polygon.length <= 3) return; // keep at least a triangle
      state.past.push(snapshot(state.planes));
      if (state.past.length > HISTORY_LIMIT) state.past.shift();
      plane.polygon.splice(action.payload.index, 1);
    },
    updatePlane: (state, action: PayloadAction<{ id: string; changes: Partial<MeasurementPlane> }>) => {
      const plane = state.planes.find((p) => p.id === action.payload.id);
      if (plane) Object.assign(plane, action.payload.changes);
    },
    deletePlane: (state, action: PayloadAction<string>) => {
      state.past.push(snapshot(state.planes));
      if (state.past.length > HISTORY_LIMIT) state.past.shift();
      state.planes = state.planes.filter((p) => p.id !== action.payload);
      if (state.selectedId === action.payload) state.selectedId = null;
      if (state.normPrompt?.planeId === action.payload) state.normPrompt = null;
      if (state.exclusionDraw.planeId === action.payload) {
        state.exclusionDraw = { active: false, planeId: null, shape: 'polygon', verts: [] };
      }
    },
    selectPlane: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setPlaneResults: (state, action: PayloadAction<MeasurementData[]>) => {
      state.results = action.payload;
    },
    setPointsView: (state, action: PayloadAction<PointsView>) => {
      state.pointsView = action.payload;
    },

    // ── Exclusion cut-outs ────────────────────────────────────────────────────
    startExclusionDraw: (state, action: PayloadAction<{ planeId: string; shape: ExclusionShape }>) => {
      state.exclusionDraw = { active: true, planeId: action.payload.planeId, shape: action.payload.shape, verts: [] };
      state.selectedId = action.payload.planeId;
      state.draw.active = false;
    },
    pushExclusionVertex: (state, action: PayloadAction<PlaneVec2>) => {
      state.exclusionDraw.verts.push(action.payload);
    },
    popExclusionVertex: (state) => {
      state.exclusionDraw.verts.pop();
    },
    cancelExclusionDraw: (state) => {
      state.exclusionDraw = { active: false, planeId: null, shape: 'polygon', verts: [] };
    },
    addExclusion: (state, action: PayloadAction<{ planeId: string; polygon: PlaneVec2[] }>) => {
      const plane = state.planes.find((p) => p.id === action.payload.planeId);
      if (!plane || action.payload.polygon.length < 3) return;
      state.past.push(snapshot(state.planes));
      if (state.past.length > HISTORY_LIMIT) state.past.shift();
      plane.exclusions = [...(plane.exclusions ?? []), action.payload.polygon];
      state.exclusionDraw = { active: false, planeId: null, shape: 'polygon', verts: [] };
    },
    removeExclusion: (state, action: PayloadAction<{ planeId: string; index: number }>) => {
      const plane = state.planes.find((p) => p.id === action.payload.planeId);
      if (!plane?.exclusions) return;
      state.past.push(snapshot(state.planes));
      if (state.past.length > HISTORY_LIMIT) state.past.shift();
      plane.exclusions = plane.exclusions.filter((_, i) => i !== action.payload.index);
    },
  },
});

export const {
  setPlaneDrawActive,
  pushPlaneVertex,
  popPlaneVertex,
  setPlaneDrawFrame,
  addPlane,
  insertPlaneVertex,
  deletePlaneVertex,
  updatePlane,
  deletePlane,
  selectPlane,
  setPlaneResults,
  setPlaneComputing,
  setNormPrompt,
  setPointsView,
  pushPlaneHistory,
  undoPlane,
  startExclusionDraw,
  pushExclusionVertex,
  popExclusionVertex,
  cancelExclusionDraw,
  addExclusion,
  removeExclusion,
} = measurementPlanesReducer.actions;
